/*
 * <personalization-field>
 *
 * A styled checkbox that drives the product's "Personalizare" variant option
 * and unlocks a text field for the engraving message.
 *
 *  - Checking the box selects the personalized variant (the theme updates the
 *    price natively) and enables the text input.
 *  - The text input is always visible but disabled until the box is ticked.
 *  - Its value is mirrored into a hidden `properties[...]` input appended to the
 *    real product <form>, so the text is attached to the order / line item.
 *  - The native variant control for this option is hidden (we drive it from the
 *    checkbox), and add-to-cart is blocked when the required field is empty.
 */
(function () {
  if (window.customElements && customElements.get('personalization-field')) return;

  class PersonalizationField extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section || '';
      this.section =
        this.closest('.shopify-section') ||
        document.getElementById('shopify-section-' + this.sectionId) ||
        document;
      this.form = document.getElementById('product-form-' + this.sectionId);

      this.optionName = (this.dataset.optionName || '').trim();
      this.optionPosition = parseInt(this.dataset.optionPosition || '0', 10);
      this.onValue = this.dataset.onValue || '';
      this.offValue = this.dataset.offValue || '';
      this.required = this.dataset.required === 'true';
      this.propertyName = (this.dataset.propertyName || 'Text personalizare').trim();
      this.maxChars = parseInt(this.dataset.max || '0', 10);
      this.galleryMediaId = (this.dataset.personalizationMediaId || '').trim();
      this._galleryTimer = null;
      this.offValues = (this.dataset.offValues || '')
        .split(',')
        .map((v) => this.norm(v))
        .filter(Boolean);

      this.card = this.querySelector('.js-perso-card');
      this.toggle = this.querySelector('.js-perso-toggle');
      this.input = this.querySelector('.js-personalization-input');
      this.errorEl = this.querySelector('.js-personalization-error');
      this.counter = this.querySelector('.js-perso-counter');
      this.hiddenInput = null;
      this._isOn = false;
      this._syncing = false;

      if (!this.input || !this.toggle || !this.optionName) return;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.toggle.addEventListener('change', this.onToggle);
      this.input.addEventListener('input', this.onInput);
      // React to variant changes coming from elsewhere (URL, sticky picker…).
      this.section.addEventListener('change', this.onVariantChange);
      this.section.addEventListener('variant-change', this.onVariantChange);
      document.addEventListener('click', this.onClickCapture, true);
      document.addEventListener('submit', this.onSubmitCapture, true);

      this.hideNativeOption();
      this.setState(this.readVariantOn());
      // If the product loads already personalized, show the personalization image.
      if (this.galleryMediaId && this._isOn) this.showPersonalizationImage();
    }

    disconnectedCallback() {
      if (this.section) {
        this.section.removeEventListener('change', this.onVariantChange);
        this.section.removeEventListener('variant-change', this.onVariantChange);
      }
      document.removeEventListener('click', this.onClickCapture, true);
      document.removeEventListener('submit', this.onSubmitCapture, true);
    }

    norm(s) {
      return (s == null ? '' : String(s))
        .toLowerCase()
        .trim()
        .normalize('NFD')
        // eslint-disable-next-line no-misleading-character-class
        .replace(/[̀-ͯ]/g, '');
    }

    // ---- native variant control (select or radios) -------------------------

    getNativeControl() {
      const scope = this.section || document;
      const selectName = 'options[' + this.optionName + ']';
      const select = [...scope.querySelectorAll('select')].find((s) => s.name === selectName);
      if (select) return { type: 'select', el: select };
      const radios = [...scope.querySelectorAll('input[type="radio"]')].filter(
        (r) => r.name === this.optionName
      );
      if (radios.length) return { type: 'radio', els: radios };
      return null;
    }

    readVariantValue() {
      const ctrl = this.getNativeControl();
      if (!ctrl) return this.valueFromVariantId();
      if (ctrl.type === 'select') return ctrl.el.value;
      const checked = ctrl.els.find((r) => r.checked);
      return checked ? checked.value : null;
    }

    valueFromVariantId() {
      if (!this.optionPosition) return null;
      const scope = this.section || document;
      const idInput = scope.querySelector('input[name="id"], select[name="id"]');
      const dataEl = scope.querySelector('variant-selects script[type="application/json"]');
      if (!idInput || !dataEl) return null;
      let variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return null;
      }
      const v = variants.find((x) => x.id === parseInt(idInput.value, 10));
      return v ? v['option' + this.optionPosition] : null;
    }

    readVariantOn() {
      const val = this.readVariantValue();
      if (val == null) return false;
      const n = this.norm(val);
      return n !== '' && this.offValues.indexOf(n) === -1;
    }

    setVariant(isOn) {
      const ctrl = this.getNativeControl();
      if (!ctrl) return;
      const target = isOn ? this.onValue : this.offValue;
      if (ctrl.type === 'select') {
        if (ctrl.el.value !== target) {
          ctrl.el.value = target;
          ctrl.el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      const radio = ctrl.els.find((r) => r.value === target);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    hideNativeOption() {
      const ctrl = this.getNativeControl();
      if (!ctrl) return;
      const el = ctrl.type === 'select' ? ctrl.el : ctrl.els[0];
      const container = el && el.closest('.product-form__input');
      if (container) container.style.display = 'none';
    }

    // ---- state -------------------------------------------------------------

    setState(isOn) {
      this._isOn = isOn;
      if (this.toggle) this.toggle.checked = isOn;
      if (this.card) this.card.classList.toggle('perso-card--active', isOn);
      if (this.input) {
        this.input.disabled = !isOn;
        if (!isOn) {
          this.input.value = '';
          this.clearError();
        }
      }
      if (isOn) {
        this.ensureHiddenInput();
        if (this.hiddenInput) this.hiddenInput.value = this.input.value;
      } else if (this.hiddenInput) {
        this.hiddenInput.remove();
        this.hiddenInput = null;
      }
      this.updateCounter();
    }

    onToggle() {
      const isOn = this.toggle.checked;
      this._syncing = true;
      this.setVariant(isOn);
      this._syncing = false;
      this.setState(isOn);
      if (this.galleryMediaId) {
        if (isOn) this.showPersonalizationImage();
        else this.restoreVariantImage();
      }
      if (isOn && this.input) {
        try {
          this.input.focus();
        } catch (e) {
          /* noop */
        }
      }
    }

    onVariantChange() {
      if (this._syncing) return;
      this.hideNativeOption();
      this.setState(this.readVariantOn());
      // Keep the personalization image showing if the shopper changes colour
      // while personalization is on (the theme would switch it to the colour image).
      if (this.galleryMediaId && this._isOn) this.showPersonalizationImage();
    }

    // ---- product image gallery --------------------------------------------

    getGalleries() {
      return (this.section || document).querySelectorAll('media-gallery');
    }

    switchGalleryTo(mediaId) {
      if (!mediaId) return;
      this.getGalleries().forEach((gallery) => {
        // Clicking the thumbnail button is identical to a user clicking it,
        // so the main slider moves exactly as the theme intends.
        const thumbBtn = gallery.querySelector('[data-target="' + mediaId + '"] button');
        if (thumbBtn) {
          thumbBtn.click();
          return;
        }
        if (typeof gallery.setActiveMedia === 'function' &&
            gallery.querySelector('[data-media-id="' + mediaId + '"]')) {
          gallery.setActiveMedia(mediaId, false);
        }
      });
    }

    showPersonalizationImage() {
      if (!this.galleryMediaId) return;
      // Small delay so we win the race against the theme's own variant-image switch.
      clearTimeout(this._galleryTimer);
      this._galleryTimer = setTimeout(() => this.switchGalleryTo(this.galleryMediaId), 90);
    }

    restoreVariantImage() {
      const id = this.variantFeaturedMediaId();
      if (id) {
        clearTimeout(this._galleryTimer);
        this._galleryTimer = setTimeout(() => this.switchGalleryTo(id), 0);
      }
    }

    variantFeaturedMediaId() {
      const scope = this.section || document;
      const idInput = scope.querySelector('input[name="id"], select[name="id"]');
      const dataEl = scope.querySelector('variant-selects script[type="application/json"]');
      if (idInput && dataEl) {
        try {
          const variants = JSON.parse(dataEl.textContent);
          const v = variants.find((x) => x.id === parseInt(idInput.value, 10));
          if (v && v.featured_media && v.featured_media.id != null) {
            return this.sectionId + '-' + v.featured_media.id;
          }
        } catch (e) {
          /* noop */
        }
      }
      // Fallback: the first media slide in the gallery (the default image).
      const gallery = this.getGalleries()[0];
      const firstSlide = gallery && gallery.querySelector('[data-media-id]');
      return firstSlide ? firstSlide.dataset.mediaId : null;
    }

    onInput() {
      if (this.hiddenInput) this.hiddenInput.value = this.input.value;
      this.clearError();
      this.updateCounter();
    }

    updateCounter() {
      if (!this.counter || !this.maxChars) return;
      this.counter.textContent = (this.input ? this.input.value.length : 0) + '/' + this.maxChars;
    }

    ensureHiddenInput() {
      if (!this.form) this.form = document.getElementById('product-form-' + this.sectionId);
      if (!this.form || this.hiddenInput) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + this.propertyName + ']';
      input.classList.add('shrine-property-input', 'personalization-property-input');
      this.form.appendChild(input);
      this.hiddenInput = input;
    }

    // ---- required validation ----------------------------------------------

    isInvalid() {
      return this._isOn && this.required && this.input && this.input.value.trim() === '';
    }

    block(e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      this.showError();
      try {
        this.input.focus();
      } catch (err) {
        /* noop */
      }
    }

    onClickCapture(e) {
      if (!this.isInvalid() || !this.form) return;
      const btn = e.target.closest(
        'button[name="add"], [type="submit"], .product-form__submit, .sticky-atc__add_button'
      );
      if (!btn) return;
      const btnForm = btn.form || btn.closest('form');
      const sticky = btn.classList && btn.classList.contains('sticky-atc__add_button');
      if (btnForm === this.form || (sticky && this.section && this.section.contains(btn))) {
        this.block(e);
      }
    }

    onSubmitCapture(e) {
      if (!this.isInvalid() || !this.form) return;
      if (e.target !== this.form) return;
      this.block(e);
    }

    showError() {
      if (this.errorEl) this.errorEl.hidden = false;
      this.classList.add('perso-invalid');
      if (this.input) this.input.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
      this.classList.remove('perso-invalid');
      if (this.input) this.input.removeAttribute('aria-invalid');
    }
  }

  customElements.define('personalization-field', PersonalizationField);
})();
